// ── BarberVerse Premium Scissors Cursor with Particle Trail ──
export function setupBarberCursor() {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouch) return;
    if (document.getElementById('bv-cursor-wrap')) return;

    // ── Inject CSS ──
    const style = document.createElement('style');
    style.textContent = `
        body { cursor: none !important; }
        a, button, input, textarea, .magnetic, [role="button"] { cursor: none !important; }

        #bv-cursor-wrap {
            position: fixed;
            top: 0; left: 0;
            pointer-events: none;
            z-index: 9999999;
            will-change: transform;
        }

        /* Main scissors SVG */
        #bv-scissors {
            position: absolute;
            width: 38px;
            height: 38px;
            transform: translate(-50%, -50%) rotate(0deg);
            transition: width 0.2s ease, height 0.2s ease, filter 0.2s ease;
            filter: drop-shadow(0 0 8px rgba(212, 175, 55, 0.5));
        }
        #bv-scissors.hovering {
            width: 50px;
            height: 50px;
            filter: drop-shadow(0 0 18px rgba(212, 175, 55, 0.9)) drop-shadow(0 0 30px rgba(212,175,55,0.4));
        }

        /* Outer ring follower */
        #bv-ring {
            position: absolute;
            width: 48px;
            height: 48px;
            border: 1.5px solid rgba(212, 175, 55, 0.5);
            border-radius: 50%;
            transform: translate(-50%, -50%);
            transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease, opacity 0.3s ease;
            mix-blend-mode: screen;
        }
        #bv-ring.hovering {
            width: 70px;
            height: 70px;
            border-color: rgba(212, 175, 55, 0.9);
        }

        /* Scissor particles */
        .bv-particle {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
            transform: translate(-50%, -50%);
            animation: bvParticleFade 0.6s ease-out forwards;
        }

        @keyframes bvParticleFade {
            0%   { opacity: 0.9; transform: translate(-50%, -50%) scale(1); }
            100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.1); }
        }

        /* Dot at tip */
        #bv-dot {
            position: absolute;
            width: 5px;
            height: 5px;
            background: #D4AF37;
            border-radius: 50%;
            transform: translate(-50%, -50%);
            box-shadow: 0 0 8px rgba(212,175,55,0.8);
        }
    `;
    document.head.appendChild(style);

    // ── DOM ──
    const wrap = document.createElement('div');
    wrap.id = 'bv-cursor-wrap';
    wrap.innerHTML = `
        <div id="bv-ring"></div>
        <svg id="bv-scissors" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <!-- Scissors body -->
            <g fill="none" stroke="#D4AF37" stroke-linecap="round" stroke-linejoin="round">
                <!-- Blade 1 -->
                <path d="M50 50 L20 20" stroke-width="6"/>
                <path d="M50 50 L80 20" stroke-width="6"/>
                <!-- Pivot ring -->
                <circle cx="50" cy="50" r="6" stroke-width="4" fill="#0a0a0a"/>
                <!-- Handles -->
                <ellipse cx="26" cy="75" rx="12" ry="8" stroke-width="4" transform="rotate(-35 26 75)"/>
                <ellipse cx="74" cy="75" rx="12" ry="8" stroke-width="4" transform="rotate(35 74 75)"/>
                <path d="M50 55 L38 68" stroke-width="4"/>
                <path d="M50 55 L62 68" stroke-width="4"/>
                <!-- Chrome highlight -->
                <path d="M20 20 L50 50" stroke-width="2" stroke="rgba(255,255,255,0.4)"/>
                <path d="M80 20 L50 50" stroke-width="2" stroke="rgba(255,255,255,0.4)"/>
            </g>
            <!-- Sheen dots -->
            <circle cx="35" cy="35" r="2" fill="rgba(255,255,255,0.5)"/>
            <circle cx="65" cy="35" r="2" fill="rgba(255,255,255,0.5)"/>
        </svg>
        <div id="bv-dot"></div>
    `;
    document.body.appendChild(wrap);

    const scissors = document.getElementById('bv-scissors');
    const ring = document.getElementById('bv-ring');
    const dot = document.getElementById('bv-dot');

    let mouseX = -200, mouseY = -200;
    let ringX = -200, ringY = -200;
    let angle = 0;
    let isHovering = false;
    let lastX = -200, lastY = -200;
    let frameId = null;

    // Particle pool
    const GOLD_SHADES = [
        'rgba(212,175,55,0.9)',
        'rgba(255,215,80,0.8)',
        'rgba(180,140,30,0.7)',
        'rgba(255,255,180,0.6)',
        'rgba(200,160,50,0.8)',
    ];
    let lastParticleTime = 0;
    const PARTICLE_INTERVAL = 30; // ms between particle spawns while moving

    function spawnParticle(x, y, vx, vy) {
        const p = document.createElement('div');
        p.className = 'bv-particle';
        const size = 3 + Math.random() * 5;
        const spread = 20 + Math.random() * 30;
        const nx = (Math.random() - 0.5);
        const ny = (Math.random() - 0.5);
        // Particle drifts opposite to movement + slight random
        const dx = (-vx * 0.5 + nx * spread).toFixed(1);
        const dy = (-vy * 0.5 + ny * spread).toFixed(1);
        const color = GOLD_SHADES[Math.floor(Math.random() * GOLD_SHADES.length)];
        const duration = 0.4 + Math.random() * 0.4;

        p.style.cssText = `
            left: ${x}px;
            top: ${y}px;
            width: ${size}px;
            height: ${size}px;
            background: ${color};
            box-shadow: 0 0 ${size * 2}px ${color};
            --dx: ${dx}px;
            --dy: ${dy}px;
            animation-duration: ${duration}s;
        `;
        wrap.appendChild(p);
        setTimeout(() => p.remove(), duration * 1000 + 50);
    }

    // Occasionally spawn a small hair/snip particle
    function spawnHairParticle(x, y) {
        const p = document.createElement('div');
        p.className = 'bv-particle';
        const len = 8 + Math.random() * 12;
        const dx = ((Math.random() - 0.5) * 40).toFixed(1);
        const dy = (10 + Math.random() * 30).toFixed(1);
        const rot = Math.random() * 360;

        p.style.cssText = `
            left: ${x}px;
            top: ${y}px;
            width: ${len}px;
            height: 1.5px;
            background: rgba(220,200,180,0.7);
            border-radius: 2px;
            --dx: ${dx}px;
            --dy: ${dy}px;
            animation-duration: ${0.5 + Math.random() * 0.4}s;
            transform: translate(-50%,-50%) rotate(${rot}deg);
        `;
        wrap.appendChild(p);
        setTimeout(() => p.remove(), 900);
    }

    function lerp(a, b, t) { return a + (b - a) * t; }

    function tick(now) {
        const speed = Math.sqrt((mouseX - lastX) ** 2 + (mouseY - lastY) ** 2);

        // Angle scissors toward movement direction
        if (speed > 1) {
            const targetAngle = Math.atan2(mouseY - lastY, mouseX - lastX) * (180 / Math.PI) + 45;
            angle = lerp(angle, targetAngle, 0.12);
        }

        // Open/close blades slightly when moving fast
        const openAmount = Math.min(speed * 0.6, 25);

        // Position scissors exactly at cursor
        wrap.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
        scissors.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;

        // Blade open effect on fast movement — morph via SVG attr if speed > 8
        // We do it via a CSS var on the SVG wrapper instead
        scissors.style.setProperty('--open', openAmount);

        // Dot stays at pointer tip
        dot.style.transform = 'translate(-50%,-50%)';

        // Ring follows with lag
        ringX = lerp(ringX, mouseX, 0.08);
        ringY = lerp(ringY, mouseY, 0.08);
        ring.style.left = `${ringX - mouseX}px`;
        ring.style.top = `${ringY - mouseY}px`;

        // Spawn gold dust while moving
        if (speed > 3 && now - lastParticleTime > PARTICLE_INTERVAL) {
            lastParticleTime = now;
            const vx = mouseX - lastX;
            const vy = mouseY - lastY;
            spawnParticle(mouseX, mouseY, vx, vy);
            // Occasionally also spawn a "snipped hair" piece
            if (Math.random() < 0.15) spawnHairParticle(mouseX, mouseY);
        }

        lastX = mouseX;
        lastY = mouseY;
        frameId = requestAnimationFrame(tick);
    }

    document.addEventListener('mousemove', (e) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
        if (!frameId) {
            lastX = mouseX;
            lastY = mouseY;
            ringX = mouseX;
            ringY = mouseY;
            frameId = requestAnimationFrame(tick);
        }
    });

    document.addEventListener('mouseleave', () => {
        wrap.style.opacity = '0';
    });
    document.addEventListener('mouseenter', () => {
        wrap.style.opacity = '1';
    });

    // Click snip effect
    document.addEventListener('click', () => {
        // Burst of gold particles
        for (let i = 0; i < 8; i++) {
            setTimeout(() => spawnParticle(mouseX, mouseY, 0, 0), i * 20);
        }
        // Snipped hairs
        for (let i = 0; i < 4; i++) {
            setTimeout(() => spawnHairParticle(mouseX, mouseY), i * 30);
        }
        // Scale punch on scissors
        scissors.style.transform = `translate(-50%, -50%) rotate(${angle}deg) scale(1.4)`;
        setTimeout(() => {
            scissors.style.transform = `translate(-50%, -50%) rotate(${angle}deg) scale(1)`;
        }, 150);
    });

    // Hover states
    function hookHover(el) {
        if (el._bvHooked) return;
        el._bvHooked = true;
        el.addEventListener('mouseenter', () => {
            isHovering = true;
            scissors.classList.add('hovering');
            ring.classList.add('hovering');
        });
        el.addEventListener('mouseleave', () => {
            isHovering = false;
            scissors.classList.remove('hovering');
            ring.classList.remove('hovering');
        });
    }

    document.querySelectorAll('a, button, input, textarea, .magnetic, [role="button"]').forEach(hookHover);
    new MutationObserver(() => {
        document.querySelectorAll('a, button, input, textarea, .magnetic, [role="button"]').forEach(hookHover);
    }).observe(document.body, { childList: true, subtree: true });
}